import _regeneratorRuntime from "C:\\Proyectos\\App%20Ecommerce\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/regenerator";
import _asyncToGenerator from "C:\\Proyectos\\App%20Ecommerce\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "C:\\Proyectos\\App%20Ecommerce\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "C:\\Proyectos\\App%20Ecommerce\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/toConsumableArray";
import React__default, { createContext, useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { stubbedPublicClientApplication, InteractionStatus, Logger, WrapperSKU, EventType, EventMessageUtils, AccountEntity, InteractionType } from '@azure/msal-browser';
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/*
 * Stubbed context implementation
 * Only used when there is no provider, which is an unsupported scenario
 */

var defaultMsalContext = {
  instance: stubbedPublicClientApplication,
  inProgress: InteractionStatus.None,
  accounts: [],
  logger: /*#__PURE__*/new Logger({})
};
var MsalContext = /*#__PURE__*/createContext(defaultMsalContext);
var MsalConsumer = MsalContext.Consumer;
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

function getChildrenOrFunction(children, args) {
  if (typeof children === "function") {
    return children(args);
  }

  return children;
}
/**
 * Helper function to determine whether 2 arrays are equal
 * Used to avoid unnecessary state updates
 * @param arrayA
 * @param arrayB
 */


function accountArraysAreEqual(arrayA, arrayB) {
  if (arrayA.length !== arrayB.length) {
    return false;
  }

  var comparisonArray = _toConsumableArray(arrayB);

  return arrayA.every(function (elementA) {
    var elementB = comparisonArray.shift();

    if (!elementA || !elementB) {
      return false;
    }

    return elementA.homeAccountId === elementB.homeAccountId && elementA.localAccountId === elementB.localAccountId && elementA.username === elementB.username;
  });
}
/* eslint-disable header/header */


var name = "@azure/msal-react";
var version = "1.0.1";
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

function MsalProvider(_ref) {
  var instance = _ref.instance,
      children = _ref.children;
  useEffect(function () {
    instance.initializeWrapperLibrary(WrapperSKU.React, version);
  }, [instance]); // Create a logger instance for msal-react with the same options as PublicClientApplication

  var logger = useMemo(function () {
    return instance.getLogger().clone(name, version);
  }, [instance]); // State hook to store accounts

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      accounts = _useState2[0],
      setAccounts = _useState2[1]; // State hook to store in progress value


  var _useState3 = useState(InteractionStatus.Startup),
      _useState4 = _slicedToArray(_useState3, 2),
      inProgress = _useState4[0],
      setInProgress = _useState4[1];

  useEffect(function () {
    var callbackId = instance.addEventCallback(function (message) {
      switch (message.eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS:
        case EventType.HANDLE_REDIRECT_END:
        case EventType.LOGIN_FAILURE:
        case EventType.SSO_SILENT_FAILURE:
        case EventType.LOGOUT_END:
        case EventType.ACQUIRE_TOKEN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_FAILURE:
          var currentAccounts = instance.getAllAccounts();

          if (!accountArraysAreEqual(currentAccounts, accounts)) {
            logger.info("MsalProvider - updating account state");
            setAccounts(currentAccounts);
          } else {
            logger.info("MsalProvider - no account changes");
          }

          break;
      }
    });
    logger.verbose("MsalProvider - Registered event callback with id: ".concat(callbackId));
    return function () {
      // Remove callback when component unmounts or accounts change
      if (callbackId) {
        logger.verbose("MsalProvider - Removing event callback ".concat(callbackId));
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, accounts, logger]);
  useEffect(function () {
    var callbackId = instance.addEventCallback(function (message) {
      var status = EventMessageUtils.getInteractionStatusFromEvent(message);

      if (status !== null) {
        logger.info("MsalProvider - ".concat(message.eventType, " results in setting inProgress to ").concat(status));
        setInProgress(status);
      }
    });
    logger.verbose("MsalProvider - Registered event callback with id: ".concat(callbackId));
    instance.handleRedirectPromise().catch(function () {
      // Errors should be handled by listening to the LOGIN_FAILURE event
      return;
    });
    return function () {
      if (callbackId) {
        logger.verbose("MsalProvider - Removing event callback ".concat(callbackId));
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, logger]);
  var contextValue = {
    instance: instance,
    inProgress: inProgress,
    accounts: accounts,
    logger: logger
  };
  return React__default.createElement(MsalContext.Provider, {
    value: contextValue
  }, children);
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Returns Msal Context values
 */


var useMsal = function useMsal() {
  return useContext(MsalContext);
};
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */


function getAccount(instance, accountIdentifiers) {
  var allAccounts = instance.getAllAccounts();

  if (allAccounts.length > 0 && (accountIdentifiers.homeAccountId || accountIdentifiers.localAccountId || accountIdentifiers.username)) {
    var matchedAccounts = allAccounts.filter(function (accountObj) {
      if (accountIdentifiers.username && accountIdentifiers.username.toLowerCase() !== accountObj.username.toLowerCase()) {
        return false;
      }

      if (accountIdentifiers.homeAccountId && accountIdentifiers.homeAccountId.toLowerCase() !== accountObj.homeAccountId.toLowerCase()) {
        return false;
      }

      if (accountIdentifiers.localAccountId && accountIdentifiers.localAccountId.toLowerCase() !== accountObj.localAccountId.toLowerCase()) {
        return false;
      }

      return true;
    });
    return matchedAccounts[0] || null;
  } else {
    return null;
  }
}
/**
 * Given 1 or more accountIdentifiers, returns the Account object if the user is signed-in
 * @param accountIdentifiers
 */


function useAccount(accountIdentifiers) {
  var _useMsal = useMsal(),
      instance = _useMsal.instance,
      inProgress = _useMsal.inProgress;

  var initialStateValue = inProgress === InteractionStatus.Startup ? null : getAccount(instance, accountIdentifiers);

  var _useState5 = useState(initialStateValue),
      _useState6 = _slicedToArray(_useState5, 2),
      account = _useState6[0],
      setAccount = _useState6[1];

  useEffect(function () {
    var currentAccount = getAccount(instance, accountIdentifiers);

    if (!AccountEntity.accountInfoIsEqual(account, currentAccount, true)) {
      setAccount(currentAccount);
    }
  }, [inProgress, accountIdentifiers, instance, account]);
  return account;
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */


function isAuthenticated(allAccounts, account, matchAccount) {
  if (matchAccount && (matchAccount.username || matchAccount.homeAccountId || matchAccount.localAccountId)) {
    return !!account;
  }

  return allAccounts.length > 0;
}
/**
 * Returns whether or not a user is currently signed-in. Optionally provide 1 or more accountIdentifiers to determine if a specific user is signed-in
 * @param matchAccount
 */


function useIsAuthenticated(matchAccount) {
  var _useMsal2 = useMsal(),
      allAccounts = _useMsal2.accounts,
      inProgress = _useMsal2.inProgress;

  var account = useAccount(matchAccount || {});
  var initialStateValue = inProgress === InteractionStatus.Startup ? false : isAuthenticated(allAccounts, account, matchAccount);

  var _useState7 = useState(initialStateValue),
      _useState8 = _slicedToArray(_useState7, 2),
      hasAuthenticated = _useState8[0],
      setHasAuthenticated = _useState8[1];

  useEffect(function () {
    setHasAuthenticated(isAuthenticated(allAccounts, account, matchAccount));
  }, [allAccounts, account, matchAccount]);
  return hasAuthenticated;
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Renders child components if user is authenticated
 * @param props
 */


function AuthenticatedTemplate(_ref2) {
  var username = _ref2.username,
      homeAccountId = _ref2.homeAccountId,
      localAccountId = _ref2.localAccountId,
      children = _ref2.children;
  var context = useMsal();
  var accountIdentifier = useMemo(function () {
    return {
      username: username,
      homeAccountId: homeAccountId,
      localAccountId: localAccountId
    };
  }, [username, homeAccountId, localAccountId]);
  var isAuthenticated = useIsAuthenticated(accountIdentifier);

  if (isAuthenticated && context.inProgress !== InteractionStatus.Startup) {
    return React__default.createElement(React__default.Fragment, null, getChildrenOrFunction(children, context));
  }

  return null;
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Renders child components if user is unauthenticated
 * @param props
 */


function UnauthenticatedTemplate(_ref3) {
  var username = _ref3.username,
      homeAccountId = _ref3.homeAccountId,
      localAccountId = _ref3.localAccountId,
      children = _ref3.children;
  var context = useMsal();
  var accountIdentifier = useMemo(function () {
    return {
      username: username,
      homeAccountId: homeAccountId,
      localAccountId: localAccountId
    };
  }, [username, homeAccountId, localAccountId]);
  var isAuthenticated = useIsAuthenticated(accountIdentifier);

  if (!isAuthenticated && context.inProgress !== InteractionStatus.Startup && context.inProgress !== InteractionStatus.HandleRedirect) {
    return React__default.createElement(React__default.Fragment, null, getChildrenOrFunction(children, context));
  }

  return null;
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Invokes a login call if a user is not currently signed-in. Failed logins can be retried using the login callback returned.
 * Optionally provide a request object to be used in the login call.
 * Optionally provide a specific user that should be logged in.
 * @param interactionType
 * @param authenticationRequest
 * @param accountIdentifiers
 */


function useMsalAuthentication(interactionType, authenticationRequest, accountIdentifiers) {
  var _useMsal3 = useMsal(),
      instance = _useMsal3.instance,
      inProgress = _useMsal3.inProgress,
      logger = _useMsal3.logger;

  var isAuthenticated = useIsAuthenticated(accountIdentifiers);

  var _useState9 = useState([null, null]),
      _useState10 = _slicedToArray(_useState9, 2),
      _useState10$ = _slicedToArray(_useState10[0], 2),
      result = _useState10$[0],
      error = _useState10$[1],
      setResponse = _useState10[1];

  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      hasBeenCalled = _useState12[0],
      setHasBeenCalled = _useState12[1];

  var login = useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(callbackInteractionType, callbackRequest) {
      var loginType, loginRequest;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              loginType = callbackInteractionType || interactionType;
              loginRequest = callbackRequest || authenticationRequest;
              _context.t0 = loginType;
              _context.next = _context.t0 === InteractionType.Popup ? 5 : _context.t0 === InteractionType.Redirect ? 7 : _context.t0 === InteractionType.Silent ? 9 : 11;
              break;

            case 5:
              logger.verbose("useMsalAuthentication - Calling loginPopup");
              return _context.abrupt("return", instance.loginPopup(loginRequest));

            case 7:
              // This promise is not expected to resolve due to full frame redirect
              logger.verbose("useMsalAuthentication - Calling loginRedirect");
              return _context.abrupt("return", instance.loginRedirect(loginRequest).then(null));

            case 9:
              logger.verbose("useMsalAuthentication - Calling ssoSilent");
              return _context.abrupt("return", instance.ssoSilent(loginRequest));

            case 11:
              throw "Invalid interaction type provided.";

            case 12:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x, _x2) {
      return _ref4.apply(this, arguments);
    };
  }(), [instance, interactionType, authenticationRequest, logger]);
  useEffect(function () {
    var callbackId = instance.addEventCallback(function (message) {
      switch (message.eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS:
          if (message.payload) {
            setResponse([message.payload, null]);
          }

          break;

        case EventType.LOGIN_FAILURE:
        case EventType.SSO_SILENT_FAILURE:
          if (message.error) {
            setResponse([null, message.error]);
          }

          break;
      }
    });
    logger.verbose("useMsalAuthentication - Registered event callback with id: ".concat(callbackId));
    return function () {
      if (callbackId) {
        logger.verbose("useMsalAuthentication - Removing event callback ".concat(callbackId));
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, logger]);
  useEffect(function () {
    if (!hasBeenCalled && !error && !isAuthenticated && inProgress === InteractionStatus.None) {
      logger.info("useMsalAuthentication - No user is authenticated, attempting to login"); // Ensure login is only called one time from within this hook, any subsequent login attempts should use the callback returned

      setHasBeenCalled(true);
      login().catch(function () {
        // Errors are handled by the event handler above
        return;
      });
    }
  }, [isAuthenticated, inProgress, error, hasBeenCalled, login, logger]);
  return {
    login: login,
    result: result,
    error: error
  };
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Attempts to authenticate user if not already authenticated, then renders child components
 * @param props
 */


function MsalAuthenticationTemplate(_ref5) {
  var interactionType = _ref5.interactionType,
      username = _ref5.username,
      homeAccountId = _ref5.homeAccountId,
      localAccountId = _ref5.localAccountId,
      authenticationRequest = _ref5.authenticationRequest,
      LoadingComponent = _ref5.loadingComponent,
      ErrorComponent = _ref5.errorComponent,
      children = _ref5.children;
  var accountIdentifier = useMemo(function () {
    return {
      username: username,
      homeAccountId: homeAccountId,
      localAccountId: localAccountId
    };
  }, [username, homeAccountId, localAccountId]);
  var context = useMsal();
  var msalAuthResult = useMsalAuthentication(interactionType, authenticationRequest, accountIdentifier);
  var isAuthenticated = useIsAuthenticated(accountIdentifier);

  if (msalAuthResult.error && context.inProgress === InteractionStatus.None) {
    if (!!ErrorComponent) {
      return React__default.createElement(ErrorComponent, Object.assign({}, msalAuthResult));
    }

    throw msalAuthResult.error;
  }

  if (isAuthenticated) {
    return React__default.createElement(React__default.Fragment, null, getChildrenOrFunction(children, msalAuthResult));
  }

  if (!!LoadingComponent && context.inProgress !== InteractionStatus.None) {
    return React__default.createElement(LoadingComponent, Object.assign({}, context));
  }

  return null;
}
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Higher order component wraps provided component with msal by injecting msal context values into the component's props
 * @param Component
 */


var withMsal = function withMsal(Component) {
  var ComponentWithMsal = function ComponentWithMsal(props) {
    var msal = useMsal();
    return React__default.createElement(Component, Object.assign({}, props, {
      msalContext: msal
    }));
  };

  var componentName = Component.displayName || Component.name || "Component";
  ComponentWithMsal.displayName = "withMsal(".concat(componentName, ")");
  return ComponentWithMsal;
};

export { AuthenticatedTemplate, MsalAuthenticationTemplate, MsalConsumer, MsalContext, MsalProvider, UnauthenticatedTemplate, useAccount, useIsAuthenticated, useMsal, useMsalAuthentication, withMsal };